import { type FormEvent, type ReactNode, useRef, useState } from 'react'
import {
  type AttentionItem,
  type Connectivity,
  type NetstatusData,
  type NetstatusParams,
  type StatusCard,
  useDashboardNetwork,
  useDashboardPos,
  useNetstatus,
} from '../../../features/dashboard/use-dashboard'
import { Btn, th, utc } from './controls'
import { Header, LedgerLink, locPath, mono, Note, Section, SkeletonRows, serif } from './ui'

const RANGES = ['1d', '7d', '30d'] as const
type Range = (typeof RANGES)[number]
type Level = 'online' | 'degraded' | 'offline'
const LEVELS: Level[] = ['online', 'degraded', 'offline']

// Semantic colors, deliberately apart from the terracotta accent.
const LEVEL_COLOR: Record<Connectivity, string> = {
  online: 'var(--v3-ok)',
  degraded: 'var(--v3-warn)',
  offline: 'var(--v3-bad)',
  unknown: 'var(--v3-off)',
}
const LEVEL_LABEL: Record<Connectivity, string> = {
  online: 'Online',
  degraded: 'Degraded',
  offline: 'Offline',
  unknown: 'Unknown',
}

const nf = (n: number) => n.toLocaleString('en-US')

function Level({ level }: { level: Connectivity }) {
  const color = LEVEL_COLOR[level]
  // online: solid; degraded: half; offline: solid ring-in-ring; unknown: hollow (shape, not colour alone)
  const dot =
    level === 'online' || level === 'offline'
      ? { background: color }
      : level === 'degraded'
        ? {
            background: `linear-gradient(90deg, ${color} 50%, transparent 50%)`,
            boxShadow: `inset 0 0 0 1.5px ${color}`,
          }
        : { boxShadow: `inset 0 0 0 1.5px ${color}` }
  return (
    <span
      className="inline-flex items-center gap-2 text-[13px]"
      style={{ color, ...serif, fontStyle: level === 'unknown' ? 'italic' : 'normal' }}
    >
      <span aria-hidden="true" className="size-[8px] rounded-full" style={dot} />
      {LEVEL_LABEL[level]}
    </span>
  )
}

function Retry({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className="border-b border-[var(--color-border)] py-6">
      <Note tone="error">The report could not read {what}.</Note>
      <Btn onClick={onRetry}>Try again</Btn>
    </div>
  )
}

function Tabs<T extends string>({
  value,
  options,
  onChange,
  label,
  render,
}: {
  value: T
  options: readonly T[]
  onChange: (v: T) => void
  label: string
  render?: (v: T) => ReactNode
}) {
  return (
    <fieldset aria-label={label} className="m-0 flex min-w-0 flex-wrap gap-x-4 border-0 p-0 text-[13px]">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          aria-pressed={o === value}
          onClick={() => onChange(o)}
          className={`-mb-px cursor-pointer border-b-2 pb-1 transition-colors focus-visible:outline-2 focus-visible:outline-[var(--color-accent)] ${
            o === value
              ? 'border-[var(--color-accent)] font-semibold text-[var(--color-fg)]'
              : 'border-transparent text-[var(--color-muted)] hover:text-[var(--color-fg)]'
          }`}
        >
          {render ? render(o) : o}
        </button>
      ))}
    </fieldset>
  )
}

/** A ruled tally: serif figure over a small-caps caption, optional footnote. */
function Tally({ label, value, note, color }: { label: string; value: ReactNode; note?: ReactNode; color?: string }) {
  return (
    <div className="border-l border-[var(--color-border)] pl-5 first:border-l-0 first:pl-0">
      <p
        className="text-[38px] font-semibold leading-none tabular-nums"
        style={{ ...serif, color: color ?? 'var(--color-fg)' }}
      >
        {value}
      </p>
      <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-muted)]">{label}</p>
      {note ? (
        <p className="mt-1 text-[13px] italic text-[var(--color-muted)]" style={serif}>
          {note}
        </p>
      ) : null}
    </div>
  )
}

function TallyRow({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-x-2 gap-y-6 py-6 sm:grid-cols-4">{children}</div>
}

/** Proportion as a ruled column chart: a thin bar split by tone with a printed legend. */
function Proportion({ parts }: { parts: { label: string; value: number; color: string }[] }) {
  const total = parts.reduce((s, p) => s + p.value, 0)
  return (
    <div>
      <div
        role="img"
        aria-label={parts.map((p) => `${p.label} ${p.value}`).join(', ')}
        className="flex h-[10px] gap-px overflow-hidden bg-[var(--color-border)]"
      >
        {parts.map((p) =>
          p.value > 0 && total > 0 ? (
            <div key={p.label} style={{ width: `${(p.value / total) * 100}%`, background: p.color }} />
          ) : null,
        )}
      </div>
      <ul className="m-0 mt-2 flex list-none flex-wrap gap-x-5 gap-y-1 p-0 text-[13px] text-[var(--color-muted)]">
        {parts.map((p) => (
          <li key={p.label} className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="size-[7px] rounded-full" style={{ background: p.color }} />
            {p.label}{' '}
            <span className="tabular-nums text-[var(--color-fg)]" style={serif}>
              {nf(p.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Spark({ points }: { points: number[] }) {
  if (points.length < 2) return null
  const min = Math.min(...points)
  const span = Math.max(...points) - min || 1
  const d = points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'}${((i / (points.length - 1)) * 120).toFixed(1)},${(26 - ((p - min) / span) * 24).toFixed(1)}`,
    )
    .join(' ')
  return (
    <svg viewBox="0 0 120 28" width="120" height="28" role="img" aria-label="Uptime trend" className="mt-2 block">
      <path d={d} fill="none" stroke="var(--color-muted)" strokeWidth="1.25" strokeLinejoin="round" />
    </svg>
  )
}

// ---------- netstatus ----------

const pickCards = (d: NetstatusData, iface: NetstatusParams['wanInterface']) =>
  iface === 'wan1' ? d.wan1Cards : iface === 'wan2' ? d.wan2Cards : d.wanCards
const countOf = (cards: StatusCard[], l: Level) => cards.find((c) => c.status === l)

function CardStrip({
  title,
  cards,
  active,
  onPick,
}: {
  title: string
  cards: StatusCard[]
  active?: Level
  onPick: (l: Level | undefined) => void
}) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted)]">{title}</p>
      <div className="mt-2 grid grid-cols-3 border-y border-[var(--color-border)]">
        {LEVELS.map((l) => {
          const c = countOf(cards, l)
          const on = active === l
          return (
            <button
              key={l}
              type="button"
              aria-pressed={on}
              aria-label={`${title}: ${LEVEL_LABEL[l]}, ${c?.count ?? 0}${on ? ' (filtering)' : ''}`}
              onClick={() => onPick(on ? undefined : l)}
              className={`cursor-pointer border-l border-[var(--color-border)] px-4 py-4 text-left transition-colors first:border-l-0 hover:bg-[var(--color-surface)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-accent)] ${
                on ? 'bg-[var(--color-surface-2)] shadow-[inset_0_-3px_0_var(--color-accent)]' : ''
              }`}
            >
              <span
                className="block text-[32px] font-semibold leading-none tabular-nums"
                style={{ ...serif, color: LEVEL_COLOR[l] }}
              >
                {nf(c?.count ?? 0)}
              </span>
              <span className="mt-2 block text-[12px] text-[var(--color-muted)]">
                {LEVEL_LABEL[l]} <span className="tabular-nums">· {Math.round(c?.pct ?? 0)}%</span>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function Netstatus() {
  const [status, setStatus] = useState<Level | undefined>()
  const [wanStatus, setWanStatus] = useState<Level | undefined>()
  const [wanInterface, setWanInterface] = useState<'wan1' | 'wan2' | undefined>()
  const [draft, setDraft] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const q = useNetstatus({ status, wanStatus, wanInterface, search: search || undefined, page })
  // Hold the previous page on screen while the next one loads, so filtering never blanks the report.
  const last = useRef<NetstatusData | undefined>(undefined)
  if (q.data) last.current = q.data
  const d = q.data ?? last.current
  const refreshing = q.isFetching && !!d

  const reset =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v)
      setPage(1)
    }
  const submit = (e: FormEvent) => {
    e.preventDefault()
    setSearch(draft.trim())
    setPage(1)
  }
  const filtered = !!(status || wanStatus || search)
  const clear = () => {
    setStatus(undefined)
    setWanStatus(undefined)
    setWanInterface(undefined)
    setDraft('')
    setSearch('')
    setPage(1)
  }

  const pageSize = d?.pageSize || 25
  const pages = d ? Math.max(1, Math.ceil(d.total / pageSize)) : 1
  const from = d && d.total > 0 ? (d.page - 1) * pageSize + 1 : 0
  const to = d ? Math.min(d.total, from + d.locations.length - 1) : 0

  return (
    <Section
      title="I. Connectivity"
      note="Where every store stands right now. Select a tally to read only those stores below."
      aside={d ? <span className="text-[var(--color-muted)]">as of {utc(d.cachedAt)}</span> : undefined}
    >
      {q.isError && !d ? (
        <Retry what="fleet connectivity" onRetry={() => void q.refetch()} />
      ) : !d ? (
        <SkeletonRows n={5} />
      ) : (
        <>
          <div className="mt-5 grid gap-8 lg:grid-cols-2">
            <CardStrip title="Store connectivity" cards={d.cards} active={status} onPick={reset(setStatus)} />
            <div>
              <CardStrip
                title={`WAN links${wanInterface ? ` · ${wanInterface.toUpperCase()}` : ' · all'}`}
                cards={pickCards(d, wanInterface)}
                active={wanStatus}
                onPick={reset(setWanStatus)}
              />
              <div className="mt-2 flex items-center gap-3 text-[12px] text-[var(--color-muted)]">
                <span>Link</span>
                <Tabs
                  label="WAN interface"
                  value={wanInterface ?? 'all'}
                  options={['all', 'wan1', 'wan2'] as const}
                  onChange={(v) => reset(setWanInterface)(v === 'all' ? undefined : v)}
                  render={(v) => (v === 'all' ? 'Both' : v.toUpperCase())}
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-end justify-between gap-4 border-b border-[var(--color-border)] pb-3">
            <p className="text-[13px] text-[var(--color-muted)]" aria-live="polite">
              <span className="text-[var(--color-fg)]">{nf(d.total)}</span> {d.total === 1 ? 'store' : 'stores'}
              {filtered ? ' match' : ''}
              {d.total > 0 ? (
                <>
                  , showing {nf(from)}–{nf(to)}
                </>
              ) : null}
              {refreshing ? <span className="italic"> · updating…</span> : null}
            </p>
            <form onSubmit={submit} className="flex items-end gap-4">
              <input
                type="search"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Search code or name"
                aria-label="Search stores"
                className="w-[200px] border-0 border-b border-[var(--color-border)] bg-transparent pb-1 text-[13.5px] text-[var(--color-fg)] placeholder:italic placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none"
              />
              <Btn type="submit">Search</Btn>
              {filtered ? <Btn onClick={clear}>Clear filters</Btn> : null}
            </form>
          </div>

          {q.isError ? (
            <Retry what="this page of stores" onRetry={() => void q.refetch()} />
          ) : d.locations.length === 0 ? (
            <Note>{filtered ? 'No store matches those filters.' : 'No stores on record.'}</Note>
          ) : (
            <div className={`overflow-x-auto transition-opacity ${refreshing ? 'opacity-60' : ''}`}>
              <table className="w-full min-w-[640px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-[var(--v3-rule-strong)]">
                    <th scope="col" className={`${th} w-[90px]`}>
                      Code
                    </th>
                    <th scope="col" className={th}>
                      Store
                    </th>
                    <th scope="col" className={th}>
                      Connectivity
                    </th>
                    <th scope="col" className={th}>
                      WAN 1
                    </th>
                    <th scope="col" className={th}>
                      WAN 2
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {d.locations.map((l) => (
                    <tr
                      key={l.code}
                      className="h-11 border-b border-[var(--color-border)] transition-colors hover:bg-[var(--color-surface)]"
                    >
                      <td className="pr-4 text-[13px] tabular-nums text-[var(--color-muted)]" style={mono}>
                        {l.aptosStoreCode ?? l.code}
                      </td>
                      <td className="pr-4 text-[15px]" style={serif}>
                        <LedgerLink to={locPath(l.code)}>{l.name}</LedgerLink>
                        {l.city || l.state ? (
                          <span className="ml-2 text-[13px] italic text-[var(--color-muted)]">
                            {[l.city, l.state].filter(Boolean).join(', ')}
                          </span>
                        ) : null}
                      </td>
                      <td className="pr-4">
                        <Level level={l.connectivity} />
                      </td>
                      <td className="pr-4">
                        <Level level={l.wan1} />
                      </td>
                      <td className="pr-4">
                        <Level level={l.wan2} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pages > 1 ? (
            <nav aria-label="Pages" className="flex items-center justify-between gap-4 py-3 text-[13px]">
              <Btn onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={d.page <= 1 || refreshing}>
                ← Previous
              </Btn>
              <span className="italic text-[var(--color-muted)]" style={serif}>
                Page {nf(d.page)} of {nf(pages)}
              </span>
              <Btn onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={d.page >= pages || refreshing}>
                Next →
              </Btn>
            </nav>
          ) : null}
        </>
      )}
    </Section>
  )
}

// ---------- network ----------

function AttentionList({ items }: { items: AttentionItem[] }) {
  if (items.length === 0) {
    return (
      <p className="border-y border-[var(--color-border)] py-6 text-[18px] italic text-[var(--v3-ok)]" style={serif}>
        Nothing needs attention. Every tunnel on the register is reporting in.
      </p>
    )
  }
  const down = items.filter((i) => i.status === 'down').length
  return (
    <div>
      <p className="mb-2 text-[14px] italic text-[var(--color-muted)]" style={serif}>
        {nf(items.length)} {items.length === 1 ? 'store' : 'stores'} flagged: {nf(down)} down, {nf(items.length - down)}{' '}
        degraded. Down stores are listed first.
      </p>
      <ol className="m-0 max-h-[460px] list-none overflow-y-auto border-t-2 border-[var(--color-accent)] p-0">
        {items.map((a) => (
          <li
            key={`${a.storeCode}-${a.vendor}`}
            className="grid grid-cols-[auto_1fr] items-baseline gap-x-5 gap-y-0.5 border-b border-[var(--color-border)] py-3 sm:grid-cols-[84px_1fr_auto_auto]"
          >
            <span className="text-[13px] tabular-nums text-[var(--color-muted)]" style={mono}>
              {a.storeCode}
            </span>
            <span className="text-[16px]" style={serif}>
              <LedgerLink to={locPath(a.storeCode)}>{a.storeName}</LedgerLink>
            </span>
            <span className="col-start-2 text-[13px] italic text-[var(--color-muted)] sm:col-start-auto" style={serif}>
              {a.detail} · {a.vendor === 'cloudflare' ? 'Cloudflare' : 'Meraki'}
            </span>
            <span className="col-start-2 sm:col-start-auto">
              <Level level={a.status === 'down' ? 'offline' : 'degraded'} />
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}

function NetworkHealth({ range }: { range: Range }) {
  const q = useDashboardNetwork(range)
  const d = q.data
  return (
    <Section
      title="II. Network health"
      note={`Tunnels, devices and uplinks over the last ${range}, then the stores that need a person.`}
    >
      {q.isError && !d ? (
        <Retry what="network health" onRetry={() => void q.refetch()} />
      ) : !d ? (
        <SkeletonRows n={5} />
      ) : (
        <>
          <TallyRow>
            <Tally
              label="Tunnel uptime"
              value={`${d.tunnelHealth.avgUptimePct.toFixed(1)}%`}
              note={
                d.tunnelHealth.trendDelta === 0
                  ? 'Flat on the prior period'
                  : `${d.tunnelHealth.trendDelta > 0 ? 'Up' : 'Down'} ${Math.abs(d.tunnelHealth.trendDelta).toFixed(1)} pts on the prior period`
              }
            />
            <Tally
              label="Tunnels down now"
              value={nf(d.tunnelHealth.currentlyDown)}
              color={d.tunnelHealth.currentlyDown > 0 ? 'var(--v3-bad)' : 'var(--v3-ok)'}
            />
            <Tally
              label="Device issues"
              value={nf(d.deviceHealth.issueCount)}
              note={`${nf(d.deviceHealth.unreachable)} unreachable`}
              color={d.deviceHealth.issueCount > 0 ? 'var(--v3-warn)' : undefined}
            />
            <Tally label="Stores with WAN" value={nf(d.wanLinks.locationsWithWan)} />
          </TallyRow>
          <Spark points={d.tunnelHealth.sparkline} />

          <div className="mt-8 grid gap-10 lg:grid-cols-2">
            <div>
              <h3 className="mb-3 text-[16px] font-semibold" style={serif}>
                Tunnel mix{' '}
                <span className="font-normal italic text-[var(--color-muted)]">· {nf(d.tunnelMix.total)} tunnels</span>
              </h3>
              <Proportion
                parts={[
                  { label: 'Cloudflare up', value: d.tunnelMix.cloudflare.up, color: 'var(--color-fg)' },
                  {
                    label: 'Cloudflare down',
                    value: d.tunnelMix.cloudflare.total - d.tunnelMix.cloudflare.up,
                    color: 'var(--v3-bad)',
                  },
                  { label: 'Meraki up', value: d.tunnelMix.meraki.up, color: 'var(--color-muted)' },
                  {
                    label: 'Meraki down',
                    value: d.tunnelMix.meraki.total - d.tunnelMix.meraki.up,
                    color: 'var(--v3-warn)',
                  },
                ]}
              />
            </div>
            <div>
              <h3 className="mb-3 text-[16px] font-semibold" style={serif}>
                WAN links <span className="font-normal italic text-[var(--color-muted)]">· active uplink</span>
              </h3>
              <Proportion
                parts={[
                  { label: 'Primary', value: d.wanLinks.primary, color: 'var(--color-fg)' },
                  { label: 'Secondary', value: d.wanLinks.secondary, color: 'var(--color-muted)' },
                  { label: 'Tertiary', value: d.wanLinks.tertiary, color: 'var(--v3-warn)' },
                ]}
              />
            </div>
          </div>

          <div className="mt-12">
            <h3 className="flex items-baseline gap-3 text-[26px] font-semibold tracking-[-0.01em]" style={serif}>
              Attention
              <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[var(--color-accent)]">
                {d.attention.length === 0 ? 'clear' : `${nf(d.attention.length)} flagged`}
              </span>
            </h3>
            <div className="mt-3">
              <AttentionList items={d.attention} />
            </div>
          </div>
        </>
      )}
    </Section>
  )
}

// ---------- POS ----------

const REASON: Record<string, string> = {
  'not-checking-in': 'Not checking in',
  'no-activity-24h': 'No activity in 24h',
}

function PosHealth({ range }: { range: Range }) {
  const q = useDashboardPos(range)
  const d = q.data
  return (
    <Section title="III. Point of sale" note={`Terminals, check-ins and payment setup, over the last ${range}.`}>
      {q.isError && !d ? (
        <Retry what="POS health" onRetry={() => void q.refetch()} />
      ) : !d ? (
        <SkeletonRows n={5} />
      ) : (
        <>
          <TallyRow>
            <Tally
              label="Terminals online"
              value={`${nf(d.terminalStatus.online)}`}
              note={`of ${nf(d.terminalStatus.total)}`}
              color="var(--v3-ok)"
            />
            <Tally
              label="Terminals offline"
              value={nf(d.terminalStatus.offline)}
              color={d.terminalStatus.offline > 0 ? 'var(--v3-bad)' : undefined}
            />
            <Tally
              label="Stale check-ins"
              value={nf(d.staleCheckIns.count)}
              note={`across ${nf(d.staleCheckIns.affectedLocations)} ${d.staleCheckIns.affectedLocations === 1 ? 'store' : 'stores'}`}
              color={d.staleCheckIns.count > 0 ? 'var(--v3-warn)' : undefined}
            />
            <Tally
              label="Unused workstations"
              value={nf(d.unusedWorkstations.count)}
              note={`idle ${d.unusedWorkstations.days}+ days`}
            />
          </TallyRow>

          <div className="mt-4 grid gap-10 lg:grid-cols-2">
            <div>
              <h3 className="mb-3 text-[16px] font-semibold" style={serif}>
                Terminals
              </h3>
              <Proportion
                parts={[
                  { label: 'Online', value: d.terminalStatus.online, color: 'var(--v3-ok)' },
                  { label: 'Offline', value: d.terminalStatus.offline, color: 'var(--v3-bad)' },
                ]}
              />
            </div>
            <div>
              <h3 className="mb-3 text-[16px] font-semibold" style={serif}>
                Payment methods{' '}
                <span className="font-normal italic text-[var(--color-muted)]">
                  · {nf(d.paymentMethods.locationsWithAdyen)} stores on Adyen
                </span>
              </h3>
              <Proportion
                parts={[
                  { label: 'Active', value: d.paymentMethods.active, color: 'var(--v3-ok)' },
                  { label: 'Inactive', value: d.paymentMethods.inactive, color: 'var(--v3-off)' },
                ]}
              />
            </div>
          </div>

          <div className="mt-10">
            <h3 className="mb-1 text-[20px] font-semibold" style={serif}>
              Failing terminals
            </h3>
            {d.failingTerminals.length === 0 ? (
              <Note>No terminal is failing. A quiet ledger.</Note>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-[var(--v3-rule-strong)]">
                      <th scope="col" className={th}>
                        Store
                      </th>
                      <th scope="col" className={th}>
                        Terminal
                      </th>
                      <th scope="col" className={th}>
                        Reason
                      </th>
                      <th scope="col" className={th}>
                        Last activity
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.failingTerminals.map((t) => {
                      const code = t.aptosStoreCode ?? t.locationCode
                      return (
                        <tr key={t.terminalId} className="h-11 border-b border-[var(--color-border)]">
                          <td className="pr-4 text-[15px]" style={serif}>
                            {code ? (
                              <LedgerLink to={locPath(code)}>{t.locationName ?? code}</LedgerLink>
                            ) : (
                              <span className="italic text-[var(--color-muted)]">Unassigned</span>
                            )}
                            {code ? (
                              <span className="ml-2 text-[12px] text-[var(--color-muted)]" style={mono}>
                                {code}
                              </span>
                            ) : null}
                          </td>
                          <td className="pr-4 text-[13px]" style={mono}>
                            {t.model} · {t.serialNumber}
                          </td>
                          <td className="pr-4">
                            <span className="text-[13px]" style={{ color: 'var(--v3-bad)', ...serif }}>
                              {REASON[t.reason] ?? t.reason}
                            </span>
                          </td>
                          <td className="pr-4 text-[13px] tabular-nums text-[var(--color-muted)]">
                            {utc(t.lastActivityAt)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </Section>
  )
}

export function LedgerDashboard() {
  const [range, setRange] = useState<Range>('7d')
  return (
    <article>
      <Header
        eyebrow="The state of the network"
        title="Dashboard"
        standfirst="A standing report on connectivity, tunnels and point of sale across every store. Read top to bottom; what needs a person is marked."
        aside={
          <Tabs label="Report range" value={range} options={RANGES} onChange={setRange} render={(r) => `Last ${r}`} />
        }
      />
      <Netstatus />
      <NetworkHealth range={range} />
      <PosHealth range={range} />
    </article>
  )
}
